<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="config-split-view">
    <!-- Left panel: Filter and grouped list -->
    <div class="config-list-panel">
      <!-- Search filter -->
      <div class="filter-container">
        <span class="filter-input-wrapper">
          <i class="ri-search-line" aria-hidden="true"></i>
          <PrimeInputText
            v-model="filterText"
            :placeholder="$t('config.splitView.filterPlaceholder')"
            class="filter-input"
          />
        </span>
      </div>

      <!-- Grouped config list -->
      <div class="config-groups">
        <div
          v-for="group in filteredGroups"
          :key="group.id"
          class="config-group"
        >
          <!-- Group header (collapsible) -->
          <div class="group-header" @click="toggleGroup(group.id)">
            <i
              :class="
                collapsedGroups[group.id]
                  ? 'ri-arrow-right-s-line'
                  : 'ri-arrow-down-s-line'
              "
              aria-hidden="true"
            ></i>
            <span class="group-label">{{ $t(group.label) }}</span>
            <span class="group-count">{{ group.items.length }}</span>
          </div>

          <!-- Group items -->
          <div v-show="!collapsedGroups[group.id]" class="group-items">
            <div
              v-for="item in group.items"
              :key="item.key"
              class="config-list-item"
              :class="{
                'is-selected': selectedKey === item.key,
                'is-undefined': !item.isDefined,
              }"
              @click="selectItem(item.key)"
            >
              <span class="item-key">{{ item.key }}</span>
              <i
                v-if="!item.isDefined"
                class="ri-checkbox-blank-circle-line undefined-indicator"
                :title="$t('config.notDefined')"
                aria-hidden="true"
              ></i>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="filteredGroups.length === 0" class="empty-state">
          <i class="ri-search-line" aria-hidden="true"></i>
          <p>{{ $t("config.splitView.noResults") }}</p>
        </div>
      </div>
    </div>

    <!-- Right panel: Editor -->
    <div class="config-editor-panel">
      <div v-if="selectedItem" class="editor-wrapper">
        <ConfigItemEditor
          :item="selectedItem"
          :description="descriptions[selectedItem.key]"
          @update:value="handleValueUpdate"
          @define="handleDefine"
          @undefine="handleUndefine"
        />
      </div>
      <div v-else class="empty-editor">
        <i class="ri-settings-3-line" aria-hidden="true"></i>
        <p>{{ $t("config.splitView.selectConfig") }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import PrimeInputText from "primevue/inputtext";
import ConfigItemEditor from "@/components/config/ConfigItemEditor.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  configData: {
    type: Array,
    required: true,
  },
  descriptions: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:config"]);

// Local state
const filterText = ref("");
const selectedKey = ref(null);
const collapsedGroups = ref({});

// Configuration groups definition
const CONFIG_GROUPS = {
  display: {
    id: "display",
    label: "config.groups.display",
    prefixes: [
      "THEME",
      "APP_ROOT",
      "SHOW_",
      "PRIMEVUE",
      "BREADCRUMB",
      "EDIT_",
      "OPEN_",
    ],
  },
  matomo: {
    id: "matomo",
    label: "config.groups.matomo",
    prefixes: ["MATOMO_"],
  },
  sentry: {
    id: "sentry",
    label: "config.groups.sentry",
    prefixes: ["SENTRY_"],
  },
  chat: {
    id: "chat",
    label: "config.groups.chat",
    prefixes: ["CHAT_", "ROCKETCHAT_"],
  },
  search: {
    id: "search",
    label: "config.groups.search",
    prefixes: ["SEARCH_"],
  },
  pagination: {
    id: "pagination",
    label: "config.groups.pagination",
    prefixes: ["DEFAULT_ELEMENT", "ROW_PER_PAGE"],
  },
  metadata: {
    id: "metadata",
    label: "config.groups.metadata",
    keys: ["METADATA", "ASPECTS", "CONSTRAINTS", "ALLOWED_FILE_EXTENSIONS"],
  },
  security: {
    id: "security",
    label: "config.groups.security",
    keys: ["NEW_PASSWORD_RULES"],
  },
  other: {
    id: "other",
    label: "config.groups.other",
  },
};

// Determine which group a config key belongs to
const getGroupForKey = (key) => {
  for (const [groupId, group] of Object.entries(CONFIG_GROUPS)) {
    if (groupId === "other") {
      continue;
    }
    // Check exact key matches
    if (group.keys && group.keys.includes(key)) {
      return groupId;
    }
    // Check prefix matches
    if (group.prefixes) {
      for (const prefix of group.prefixes) {
        if (key.startsWith(prefix)) {
          return groupId;
        }
      }
    }
  }
  return "other";
};

// Group and filter config items
const filteredGroups = computed(() => {
  const filter = filterText.value.toLowerCase().trim();

  // Group all items
  const grouped = {};
  for (const groupId of Object.keys(CONFIG_GROUPS)) {
    grouped[groupId] = [];
  }

  for (const item of props.configData) {
    // Apply filter
    if (filter) {
      const keyMatch = item.key.toLowerCase().includes(filter);
      const descriptionKey = props.descriptions[item.key]?.descriptionKey;
      const descriptionMatch = descriptionKey
        ? t(descriptionKey).toLowerCase().includes(filter)
        : false;

      if (!keyMatch && !descriptionMatch) {
        continue;
      }
    }

    const groupId = getGroupForKey(item.key);
    grouped[groupId].push(item);
  }

  // Sort items within each group alphabetically
  for (const groupId of Object.keys(grouped)) {
    grouped[groupId].sort((a, b) => a.key.localeCompare(b.key));
  }

  // Convert to array and filter out empty groups
  const result = [];
  for (const [groupId, group] of Object.entries(CONFIG_GROUPS)) {
    if (grouped[groupId].length > 0) {
      result.push({
        id: groupId,
        label: group.label,
        items: grouped[groupId],
      });
    }
  }

  return result;
});

// Currently selected item
const selectedItem = computed(() => {
  if (!selectedKey.value) {
    return null;
  }
  return props.configData.find((item) => item.key === selectedKey.value);
});

// Toggle group collapse
const toggleGroup = (groupId) => {
  collapsedGroups.value[groupId] = !collapsedGroups.value[groupId];
};

// Select an item
const selectItem = (key) => {
  selectedKey.value = key;
};

// Handle value update from editor
const handleValueUpdate = (newValue) => {
  if (!selectedKey.value) {
    return;
  }
  const newConfig = props.configData.map((item) => {
    if (item.key === selectedKey.value) {
      return { ...item, value: newValue };
    }
    return item;
  });
  emit("update:config", newConfig);
};

// Handle define action
const handleDefine = () => {
  if (!selectedKey.value) {
    return;
  }
  const newConfig = props.configData.map((item) => {
    if (item.key === selectedKey.value) {
      return { ...item, isDefined: true };
    }
    return item;
  });
  emit("update:config", newConfig);
};

// Handle undefine action
const handleUndefine = () => {
  if (!selectedKey.value) {
    return;
  }
  const newConfig = props.configData.map((item) => {
    if (item.key === selectedKey.value) {
      const defaultValue = props.descriptions[item.key]?.default;
      return { ...item, isDefined: false, value: defaultValue };
    }
    return item;
  });
  emit("update:config", newConfig);
};

// Auto-select first item if none selected when data changes
watch(
  () => props.configData,
  () => {
    if (!selectedKey.value && props.configData.length > 0) {
      // Select first item from first non-empty group
      if (
        filteredGroups.value.length > 0 &&
        filteredGroups.value[0].items.length > 0
      ) {
        selectedKey.value = filteredGroups.value[0].items[0].key;
      }
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.config-split-view {
  display: flex;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius);
  overflow: hidden;
  background-color: var(--p-surface-card);
  min-height: 500px;
}

/* Left panel */
.config-list-panel {
  display: flex;
  flex-direction: column;
  width: 300px;
  min-width: 250px;
  border-right: 1px solid var(--p-content-border-color);
  background-color: var(--p-content-background);
}

.filter-container {
  padding: 0.75rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.filter-input-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
}

.filter-input-wrapper i {
  position: absolute;
  left: 0.75rem;
  color: var(--p-text-muted-color);
  z-index: 1;
}

.filter-input {
  width: 100%;
  padding-left: 2.25rem;
}

.config-groups {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

/* Group styling */
.config-group {
  margin-bottom: 0.25rem;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--p-text-color);
  transition: background-color 0.15s;
}

.group-header:hover {
  background-color: var(--p-surface-hover);
}

.group-header i {
  font-size: 1rem;
  color: var(--p-text-muted-color);
}

.group-label {
  flex: 1;
}

.group-count {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  background-color: var(--p-content-border-color);
  color: var(--p-text-muted-color);
  border-radius: 10px;
}

/* List items */
.group-items {
  padding-left: 0.5rem;
}

.config-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 4px;
  margin: 0.125rem 0.5rem;
  transition: background-color 0.15s;
}

.config-list-item:hover {
  background-color: var(--p-surface-hover);
}

.config-list-item.is-selected {
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
}

.config-list-item.is-selected .undefined-indicator {
  color: var(--p-primary-contrast-color);
  opacity: 0.7;
}

.config-list-item.is-undefined .item-key {
  opacity: 0.7;
}

.item-key {
  font-size: 0.85rem;
  word-break: break-all;
  flex: 1;
}

.undefined-indicator {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
  margin-left: 0.5rem;
  display: inline-block;
  line-height: 1;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--p-text-muted-color);
}

.empty-state i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.empty-state p {
  margin: 0;
  text-align: center;
  font-size: 0.9rem;
}

/* Right panel */
.config-editor-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-wrapper {
  flex: 1;
  overflow: hidden;
}

.empty-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--p-text-muted-color);
}

.empty-editor i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-editor p {
  margin: 0;
  font-size: 1rem;
}
</style>
